import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD, PAYMENT_MODES } from '../utils/constants';
import { ChevronRight, HandCoins } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const todayInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const toDisplayDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
};
const cruiseDateISO = (t) => {
  if (!t || !/^\d{2}-\d{2}-\d{4}$/.test(t)) return null;
  const [d, m, y] = t.split('-');
  return `${y}-${m}-${d}`;
};
const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const PAY_KEYS = ['razorpay_amount', 'cash_amount', 'icici_amount', 'bank_transfer_amount', 'gpay_amount'];
const paidOf = (b) => PAY_KEYS.reduce((s, k) => s + (Number(b[k]) || 0), 0);
const EMPTY_ENTRY = { owed: [], upcoming: [], payments: [], outstanding: 0, upcomingTotal: 0, unallocated: 0, position: 0, lastPaidOn: null };

// ─── Styles (report-screen conventions) ───────────────────────────────────────
const card = { background: '#fff', border: '1px solid var(--color-border)', borderRadius: 14, padding: '18px 22px', marginBottom: 18, boxShadow: '0 1px 6px rgba(10,22,40,0.06)' };
const sectionLabel = { fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 14 };
const thStyle = { padding: '8px 12px', fontWeight: 700, fontSize: 'var(--text-xs)', color: 'var(--color-ink-muted)', textTransform: 'uppercase', letterSpacing: '0.4px', textAlign: 'right', whiteSpace: 'nowrap', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-strong)' };
const thLeft = { ...thStyle, textAlign: 'left' };
const tdStyle = { padding: '8px 12px', fontSize: 'var(--text-sm)', color: 'var(--color-ink-brand-softer)', borderBottom: '1px solid var(--color-border-subtle)', verticalAlign: 'middle' };
const tdNum = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 };
const tableWrap = { overflowX: 'auto', borderRadius: 'var(--radius-lg)', border: '1px solid var(--color-border)', marginBottom: 10 };
const tblStyle = { width: '100%', borderCollapse: 'collapse', fontSize: 'var(--text-sm)' };
const fieldStyle = { padding: '8px 12px', border: '1.5px solid var(--color-border-strong)', borderRadius: 8, fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', background: '#fff', color: NAVY };
const labelStyle = { display: 'block', fontSize: '0.75rem', fontWeight: 700, color: 'var(--color-ink-muted)', marginBottom: 4 };
const statTile = { background: 'var(--color-sunken)', border: '1px solid var(--color-border)', borderRadius: 10, padding: '10px 16px', minWidth: 150 };

function Stat({ label, value, tone }) {
  return (
    <div style={statTile}>
      <div style={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-ink-muted)', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: '1rem', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: tone || NAVY }}>{value}</div>
    </div>
  );
}

// ─── Drill-in bookings table (owed / upcoming sections) ───────────────────────
// rows: [{ booking, paid, remaining }]
function BookingsTable({ rows, empty, netLabel, muted }) {
  if (rows.length === 0) {
    return <div style={{ padding: '12px 0', fontSize: '0.85rem', color: 'var(--color-ink-faint)' }}>{empty}</div>;
  }
  return (
    <div style={tableWrap}>
      <table style={tblStyle}>
        <thead>
          <tr>
            <th style={thLeft}>Cruise date</th>
            <th style={thLeft}>Booking</th>
            <th style={thLeft}>Cruise</th>
            <th style={thStyle}>{netLabel}</th>
            <th style={thStyle}>Paid</th>
            <th style={thStyle}>Remaining</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ booking: b, paid, remaining }) => (
            <tr key={b.id}>
              <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{b.date}</td>
              <td style={{ ...tdStyle, fontWeight: 600, color: NAVY }}>{b.booking_name}</td>
              <td style={tdStyle}>{b.cruise_type}</td>
              <td style={tdNum}>{fmt(b.net_total)}</td>
              <td style={tdNum}>{paid ? fmt(paid) : ''}</td>
              <td style={{ ...tdNum, fontWeight: 700, color: remaining <= 0.5 ? '#15803d' : muted ? 'var(--color-ink-muted)' : '#b45309' }}>{fmt(remaining)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Allocation editor (shared by both modals) ────────────────────────────────
// rows: [{ booking, remaining }] — owed (Balance) rows first, then upcoming
// (Pending); values: { [bookingId]: string }
function AllocationEditor({ rows, values, onChange }) {
  if (rows.length === 0) {
    return <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-faint)', padding: '6px 0' }}>No open credit bookings — the full amount stays as unallocated credit.</div>;
  }
  const firstUpcoming = rows.findIndex(r => r.booking.payment_status !== 'Balance');
  return (
    <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
      {rows.map(({ booking: b, remaining }, i) => (
        <div key={b.id}>
          {i === firstUpcoming && (
            <div style={{ padding: '4px 10px', fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--color-ink-muted)', background: 'var(--color-sunken)', borderBottom: '1px solid var(--color-border-subtle)' }}>
              Upcoming — not yet owed (advance)
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', borderBottom: '1px solid var(--color-border-subtle)', fontSize: '0.8rem' }}>
            <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: NAVY }}>
              {b.booking_name}
              <span style={{ color: 'var(--color-ink-faint)' }}> · cruise {b.date} · {b.payment_status === 'Balance' ? 'due' : 'net'} {fmt(remaining)}</span>
            </span>
            <input
              type="number" min="0" max={remaining}
              value={values[b.id] ?? ''}
              onChange={e => onChange(b.id, e.target.value)}
              placeholder="0"
              style={{ ...fieldStyle, width: 100, padding: '5px 8px', fontSize: '0.82rem', textAlign: 'right' }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

// Greedy oldest-first fill of `pool` across rows (capped per booking).
const autoFill = (rows, pool) => {
  const next = {};
  let left = pool;
  rows.forEach(({ booking, remaining }) => {
    const take = Math.max(0, Math.min(remaining, left));
    if (take > 0) { next[booking.id] = String(take); left -= take; }
  });
  return next;
};

// ─── Record-payment modal ─────────────────────────────────────────────────────
function RecordPartnerPaymentModal({ partner, openRows, onClose, onSaved }) {
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('');
  const [date, setDate] = useState(todayInput());
  const [note, setNote] = useState('');
  const [allocs, setAllocs] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allocated = openRows.reduce((s, r) => s + (Number(allocs[r.booking.id]) || 0), 0);
  const amt = Number(amount) || 0;
  const leftover = amt - allocated;

  const handleSave = async () => {
    if (!amount || !mode || !date) { setError('Amount, mode and date are required.'); return; }
    if (amt <= 0) { setError('Enter a valid amount.'); return; }
    if (allocated > amt + 0.01) { setError('Allocations exceed the payment amount.'); return; }
    const over = openRows.find(r => (Number(allocs[r.booking.id]) || 0) > r.remaining + 0.01);
    if (over) { setError(`Allocation exceeds the amount due on ${over.booking.booking_name}.`); return; }
    setSaving(true);
    const payload = openRows
      .filter(r => (Number(allocs[r.booking.id]) || 0) > 0)
      .map(r => ({ booking_id: r.booking.id, amount: Number(allocs[r.booking.id]) }));
    const { error: err } = await supabase.rpc('record_partner_payment', {
      p_partner_id: partner.id,
      p_amount: amt,
      p_mode: mode,
      p_paid_on: date,
      p_allocations: payload.length ? payload : null,
      p_note: note.trim() || null,
    });
    setSaving(false);
    if (err) { setError('Save failed: ' + err.message); return; }
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: '24px 24px 20px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)' }}>
        <h3 style={{ margin: '0 0 2px', fontSize: '1rem', fontWeight: 700, color: NAVY }}>Record Partner Payment</h3>
        <p style={{ margin: '0 0 16px', fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>{partner.name}</p>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <label style={labelStyle}>Amount (₹)</label>
            <input type="number" min="0" value={amount} autoFocus
              onChange={e => { setAmount(e.target.value); setAllocs(autoFill(openRows, Number(e.target.value) || 0)); }}
              placeholder="0" style={{ ...fieldStyle, width: 130 }} />
          </div>
          <div>
            <label style={labelStyle}>Mode</label>
            <select value={mode} onChange={e => setMode(e.target.value)} style={{ ...fieldStyle, width: 150 }}>
              <option value="">Select…</option>
              {PAYMENT_MODES.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Paid On</label>
            <input type="date" value={date} max={todayInput()} onChange={e => setDate(e.target.value)}
              style={{ ...fieldStyle, colorScheme: 'light' }} />
          </div>
        </div>

        <div style={{ marginBottom: 6 }}>
          <label style={labelStyle}>Allocate to bookings (owed first, then upcoming — amounts adjustable)</label>
          <AllocationEditor rows={openRows} values={allocs}
            onChange={(id, v) => setAllocs(a => ({ ...a, [id]: v }))} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: 'var(--color-ink-muted)', marginBottom: 14 }}>
          <span>Allocated {fmt(allocated)}</span>
          <span style={{ color: leftover < -0.01 ? '#dc2626' : leftover > 0.01 ? '#b45309' : '#15803d', fontWeight: 700 }}>
            {leftover < -0.01 ? `Over-allocated by ${fmt(-leftover)}`
              : leftover > 0.01 ? `${fmt(leftover)} stays as unallocated credit`
              : 'Fully allocated'}
          </span>
        </div>

        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Note (optional)</label>
          <input type="text" value={note} onChange={e => setNote(e.target.value)}
            placeholder="e.g. settlement for July bookings" style={{ ...fieldStyle, width: '100%' }} />
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Record Payment'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: 'none', color: 'var(--color-ink-muted)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Allocate-existing-credit modal ───────────────────────────────────────────
function AllocateCreditModal({ payment, partnerName, openRows, onClose, onSaved }) {
  const [allocs, setAllocs] = useState(() => autoFill(openRows, payment.unallocated));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const allocated = openRows.reduce((s, r) => s + (Number(allocs[r.booking.id]) || 0), 0);

  const handleSave = async () => {
    if (allocated <= 0) { setError('Nothing allocated.'); return; }
    if (allocated > payment.unallocated + 0.01) { setError('Allocations exceed the unallocated credit.'); return; }
    setSaving(true);
    const payload = openRows
      .filter(r => (Number(allocs[r.booking.id]) || 0) > 0)
      .map(r => ({ booking_id: r.booking.id, amount: Number(allocs[r.booking.id]) }));
    const { error: err } = await supabase.rpc('allocate_partner_payment', {
      p_payment_id: payment.id,
      p_allocations: payload,
    });
    setSaving(false);
    if (err) { setError('Allocation failed: ' + err.message); return; }
    onSaved();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(10,22,40,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}
         onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '88vh', overflowY: 'auto', padding: '24px 24px 20px', boxShadow: '0 8px 40px rgba(10,22,40,0.22)' }}>
        <h3 style={{ margin: '0 0 2px', fontSize: '1rem', fontWeight: 700, color: NAVY }}>Allocate Credit</h3>
        <p style={{ margin: '0 0 14px', fontSize: '0.8rem', color: 'var(--color-ink-muted)' }}>
          {partnerName} — payment of {fmt(payment.amount)} on {toDisplayDate(payment.paid_on)} ({payment.mode}), {fmt(payment.unallocated)} unallocated.
          Ledger entries will carry the payment's original date.
        </p>

        <AllocationEditor rows={openRows} values={allocs}
          onChange={(id, v) => setAllocs(a => ({ ...a, [id]: v }))} />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums', color: 'var(--color-ink-muted)', margin: '8px 0 16px' }}>
          <span>Allocated {fmt(allocated)}</span>
          <span style={{ fontWeight: 700, color: allocated > payment.unallocated + 0.01 ? '#dc2626' : 'var(--color-ink-muted)' }}>
            of {fmt(payment.unallocated)} available
          </span>
        </div>

        {error && <p style={{ color: '#dc2626', fontSize: '0.8rem', marginBottom: 12 }}>{error}</p>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleSave} disabled={saving}
            style={{ flex: 1, background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Allocate'}
          </button>
          <button onClick={onClose}
            style={{ flex: 1, background: 'none', color: 'var(--color-ink-muted)', border: '1px solid var(--color-border-strong)', borderRadius: 8, padding: '9px 0', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function PartnerLedger({ onNavigate }) {
  const [partners, setPartners] = useState([]);
  const [creditBookings, setCreditBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [payModal, setPayModal] = useState(false);
  const [allocModal, setAllocModal] = useState(null); // payment row

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const [pRes, bRes, payRes, aRes] = await Promise.all([
      // archived_at is read, not filtered: the ledger is keyed by bookings.partner_id,
      // so an archived partner that still has owed bookings, upcoming bookings or
      // unallocated credit must keep showing here (money does not disappear with the
      // name). It is only dropped from the "Show all partners" padding below.
      supabase.from('partners').select('id, name, phone_number, archived_at').order('name'),
      supabase.from('bookings')
        .select('id, partner_id, booking_name, date, cruise_type, net_total, payment_status, razorpay_amount, cash_amount, icici_amount, bank_transfer_amount, gpay_amount')
        .not('partner_id', 'is', null)
        .in('payment_status', ['Pending', 'Balance'])
        .limit(2000),
      supabase.from('partner_payments').select('*').order('paid_on', { ascending: false }).limit(2000),
      supabase.from('partner_payment_allocations').select('payment_id, booking_id, amount').limit(5000),
    ]);
    setPartners(pRes.data || []);
    setCreditBookings(bRes.data || []);
    setPayments(payRes.data || []);
    setAllocations(aRes.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Per-partner rollup. Outstanding derives from bookings; credit from payments.
  // Only Balance bookings are owed (cruise sailed — the nightly job flips Pending
  // → Balance after the cruise date). Pending rows are held enquiries for future
  // cruises: tracked as "upcoming" and kept out of outstanding / position.
  const ledger = useMemo(() => {
    const allocByPayment = new Map();
    allocations.forEach(a => allocByPayment.set(a.payment_id, (allocByPayment.get(a.payment_id) || 0) + Number(a.amount)));

    const byPartner = new Map();
    const entry = (pid) => {
      if (!byPartner.has(pid)) byPartner.set(pid, { owed: [], upcoming: [], payments: [], outstanding: 0, upcomingTotal: 0, unallocated: 0, lastPaidOn: null });
      return byPartner.get(pid);
    };
    creditBookings.forEach(b => {
      const remaining = (Number(b.net_total) || 0) - paidOf(b);
      const e = entry(b.partner_id);
      const row = { booking: b, remaining, paid: paidOf(b) };
      if (b.payment_status === 'Balance') { e.owed.push(row); e.outstanding += remaining; }
      else { e.upcoming.push(row); e.upcomingTotal += remaining; }
    });
    payments.forEach(p => {
      const allocated = allocByPayment.get(p.id) || 0;
      const unallocated = Number(p.amount) - allocated;
      const e = entry(p.partner_id);
      e.payments.push({ ...p, allocated, unallocated });
      e.unallocated += unallocated;
      if (!e.lastPaidOn || p.paid_on > e.lastPaidOn) e.lastPaidOn = p.paid_on;
    });
    const byCruiseDate = (a, b2) => (cruiseDateISO(a.booking.date) || '9999') < (cruiseDateISO(b2.booking.date) || '9999') ? -1 : 1;
    byPartner.forEach(e => {
      e.owed.sort(byCruiseDate);
      e.upcoming.sort(byCruiseDate);
      e.position = e.outstanding - e.unallocated;
    });
    return byPartner;
  }, [creditBookings, payments, allocations]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return partners
      .map(p => ({ partner: p, e: ledger.get(p.id) }))
      .filter(({ partner, e }) => {
        if (q && !partner.name.toLowerCase().includes(q)) return false;
        const active = !!e && (Math.abs(e.position) >= 0.5 || e.owed.length > 0 || e.upcoming.length > 0 || e.payments.length > 0);
        // Archived partners appear only while they still have activity; "Show all"
        // never surfaces an archived name on its own.
        if (partner.archived_at) return active;
        if (showAll) return true;
        return active;
      });
  }, [partners, ledger, search, showAll]);

  const totals = useMemo(() => {
    let outstanding = 0, upcoming = 0, credit = 0, owing = 0;
    ledger.forEach(e => {
      outstanding += e.outstanding;
      upcoming += e.upcomingTotal;
      credit += e.unallocated;
      if (e.position > 0.5) owing += 1;
    });
    return { outstanding, upcoming, credit, owing };
  }, [ledger]);

  const selected = selectedId ? partners.find(p => p.id === selectedId) : null;
  const selEntry = selectedId ? (ledger.get(selectedId) || EMPTY_ENTRY) : null;
  // Allocation order: owed (Balance) bookings first, then upcoming — so a payment
  // settles what is due before an advance against a future cruise.
  const openRows = selEntry ? [...selEntry.owed, ...selEntry.upcoming].filter(r => r.remaining > 0.5) : [];

  return (
    <div>
      <div className="back-row">
        {selected
          ? <button className="back-btn" onClick={() => setSelectedId(null)}>← All Partners</button>
          : <button className="back-btn" onClick={() => onNavigate('partners')}>← Partners</button>}
      </div>

      {!selected && (
        <>
          <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, marginBottom: 4 }}>Partner Ledger</h2>
          <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginBottom: 20 }}>
            Outstanding is Balance bookings only — cruises that have sailed, net after TAC, minus unallocated payments.
            Pending bookings for upcoming cruises are shown separately and are not yet owed.
          </p>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Stat label="Total outstanding" value={fmt(totals.outstanding)} tone="#b45309" />
            <Stat label="Upcoming (not yet owed)" value={fmt(totals.upcoming)} tone="var(--color-ink-muted)" />
            <Stat label="Unallocated credit" value={fmt(totals.credit)} tone="#15803d" />
            <Stat label="Partners owing" value={String(totals.owing)} />
          </div>

          <div style={card}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
              <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search partners…"
                style={{ ...fieldStyle, background: 'var(--color-sunken)', flex: 1, minWidth: 160, maxWidth: 280, padding: '6px 10px', fontSize: '0.82rem' }} />
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: 'var(--color-ink-muted)', cursor: 'pointer' }}>
                <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
                Show all partners
              </label>
            </div>
            {loading ? (
              <div style={{ padding: '24px 0', fontSize: '0.85rem', color: 'var(--color-ink-muted)' }}>Loading…</div>
            ) : rows.length === 0 ? (
              <div style={{ padding: '24px 0', fontSize: '0.85rem', color: 'var(--color-ink-faint)' }}>No partners with activity.</div>
            ) : (
              <div style={tableWrap}>
                <table style={tblStyle}>
                  <thead>
                    <tr>
                      <th style={thLeft}>Partner</th>
                      <th style={thStyle}>Owed bookings</th>
                      <th style={thStyle}>Outstanding</th>
                      <th style={thStyle}>Upcoming</th>
                      <th style={thStyle}>Unallocated credit</th>
                      <th style={thStyle}>Position</th>
                      <th style={thStyle}>Last payment</th>
                      <th style={thStyle} aria-label="Open" />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(({ partner, e }) => {
                      const en = e || EMPTY_ENTRY;
                      return (
                        <tr key={partner.id} onClick={() => setSelectedId(partner.id)} style={{ cursor: 'pointer' }}>
                          <td style={{ ...tdStyle, fontWeight: 700, color: NAVY }}>
                            {partner.name}
                            {partner.archived_at && <span className="badge badge-grey" style={{ marginLeft: 8, verticalAlign: 'middle' }}>Archived</span>}
                          </td>
                          <td style={tdNum}>{en.owed.length || ''}</td>
                          <td style={{ ...tdNum, color: en.outstanding > 0.5 ? '#b45309' : 'var(--color-ink-faint)' }}>{en.outstanding ? fmt(en.outstanding) : ''}</td>
                          <td style={{ ...tdNum, fontWeight: 500, color: 'var(--color-ink-muted)' }}>
                            {en.upcomingTotal > 0.5 ? fmt(en.upcomingTotal) : ''}
                          </td>
                          <td style={{ ...tdNum, color: en.unallocated > 0.5 ? '#15803d' : 'var(--color-ink-faint)' }}>{en.unallocated ? fmt(en.unallocated) : ''}</td>
                          <td style={{ ...tdNum, fontWeight: 700, color: en.position > 0.5 ? '#b45309' : en.position < -0.5 ? '#15803d' : 'var(--color-ink-faint)' }}>
                            {Math.abs(en.position) < 0.5 ? '—' : en.position > 0 ? fmt(en.position) : `${fmt(-en.position)} cr`}
                          </td>
                          <td style={{ ...tdNum, fontWeight: 500 }}>{en.lastPaidOn ? toDisplayDate(en.lastPaidOn) : ''}</td>
                          <td style={{ ...tdStyle, width: 30 }}><ChevronRight size={15} strokeWidth={2} aria-hidden="true" /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {selected && selEntry && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
            <div>
              <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: NAVY, margin: 0 }}>
                {selected.name}
                {selected.archived_at && <span className="badge badge-grey" style={{ marginLeft: 10, verticalAlign: 'middle' }}>Archived</span>}
              </h2>
              {selected.phone_number && <div style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', marginTop: 2 }}>{selected.phone_number}</div>}
            </div>
            <button
              onClick={() => setPayModal(true)}
              style={{ background: NAVY, color: GOLD, border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <HandCoins size={16} strokeWidth={1.75} aria-hidden="true" />Record Payment
            </button>
          </div>

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
            <Stat label="Outstanding" value={fmt(selEntry.outstanding)} tone={selEntry.outstanding > 0.5 ? '#b45309' : NAVY} />
            <Stat label="Upcoming (not yet owed)" value={fmt(selEntry.upcomingTotal)} tone="var(--color-ink-muted)" />
            <Stat label="Unallocated credit" value={fmt(selEntry.unallocated)} tone={selEntry.unallocated > 0.5 ? '#15803d' : NAVY} />
            <Stat label="Net position"
              value={Math.abs(selEntry.position) < 0.5 ? '—' : selEntry.position > 0 ? fmt(selEntry.position) : `${fmt(-selEntry.position)} credit`}
              tone={selEntry.position > 0.5 ? '#b45309' : selEntry.position < -0.5 ? '#15803d' : NAVY} />
          </div>

          <div style={card}>
            <div style={sectionLabel}>Owed — Balance bookings (oldest cruise first)</div>
            <BookingsTable rows={selEntry.owed} empty="No balance bookings — nothing owed." netLabel="Net (owed)" />
          </div>

          <div style={card}>
            <div style={sectionLabel}>Upcoming — Pending bookings, not yet owed (next cruise first)</div>
            <BookingsTable rows={selEntry.upcoming} empty="No pending bookings for upcoming cruises." netLabel="Net" muted />
          </div>

          <div style={card}>
            <div style={sectionLabel}>Payments</div>
            {selEntry.payments.length === 0 ? (
              <div style={{ padding: '12px 0', fontSize: '0.85rem', color: 'var(--color-ink-faint)' }}>No payments recorded.</div>
            ) : (
              <div style={tableWrap}>
                <table style={tblStyle}>
                  <thead>
                    <tr>
                      <th style={thLeft}>Paid on</th>
                      <th style={thStyle}>Amount</th>
                      <th style={thLeft}>Mode</th>
                      <th style={thStyle}>Allocated</th>
                      <th style={thStyle}>Unallocated</th>
                      <th style={thLeft}>Note</th>
                      <th style={thLeft}>By</th>
                      <th style={thLeft} aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {selEntry.payments.map(p => (
                      <tr key={p.id}>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{toDisplayDate(p.paid_on)}</td>
                        <td style={{ ...tdNum, fontWeight: 700 }}>{fmt(p.amount)}</td>
                        <td style={tdStyle}>{p.mode}</td>
                        <td style={tdNum}>{p.allocated ? fmt(p.allocated) : ''}</td>
                        <td style={{ ...tdNum, color: p.unallocated > 0.5 ? '#15803d' : 'var(--color-ink-faint)' }}>{p.unallocated > 0.5 ? fmt(p.unallocated) : ''}</td>
                        <td style={{ ...tdStyle, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.note || ''}</td>
                        <td style={{ ...tdStyle, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.created_by || ''}</td>
                        <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                          {p.unallocated > 0.5 && openRows.length > 0 && (
                            <button onClick={() => setAllocModal(p)}
                              style={{ fontSize: '0.68rem', fontWeight: 700, color: NAVY, background: 'var(--color-sunken)', border: '1px solid var(--color-border-strong)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer' }}>
                              Allocate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {payModal && selected && (
        <RecordPartnerPaymentModal
          partner={selected}
          openRows={openRows}
          onClose={() => setPayModal(false)}
          onSaved={() => { setPayModal(false); fetchAll(); }}
        />
      )}
      {allocModal && selected && (
        <AllocateCreditModal
          payment={allocModal}
          partnerName={selected.name}
          openRows={openRows}
          onClose={() => setAllocModal(null)}
          onSaved={() => { setAllocModal(null); fetchAll(); }}
        />
      )}
    </div>
  );
}
